import { useEffect, useMemo, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { Brain, Check, Play, ShieldCheck, X } from '@phosphor-icons/react';
import { drumLanguageAtom, drumSelectedCognitiveAtom } from '../../../shared/store/drumAppAtoms';
import { useDrumMidi } from '../../../shared/store/MidiContext';
import { createCognitiveReport, type CognitiveReport } from '../../../shared/data/cognitiveEngine';
import { readFeedbackHistory, readPreviousReport, saveCognitiveReport } from '../../../shared/data/cognitiveReportsStorage';
import { DRUMS, MEMORY_AGE_PROFILES, MidiStatus, makeMemorySequence, playCognitiveAnswerTone, type MemoryAgeProfile } from './cognitiveGameShared';
import CognitiveAnswerEffect, { type AnswerEffectResult } from './CognitiveAnswerEffect';
import CognitiveReportView from './CognitiveReportView';

const ACTIVITY_ID = 'color-sequence-drum';
const ACTIVITY_VERSION = '2.1.0';

type Phase = 'show' | 'gap' | 'answer' | 'transition' | 'complete';

interface CompletedTrial {
  completed: true;
  sequence: string[];
  correct: boolean;
  errorType: 'sequence' | 'timeout' | null;
  responseTimes: number[];
  inputMode: 'touch' | 'midi';
}

function ColorCognitionGame() {
  const language = useAtomValue(drumLanguageAtom);
  const activity = useAtomValue(drumSelectedCognitiveAtom);
  const midi = useDrumMidi();
  const navigate = useNavigate();
  const onExit = () => navigate('/drum/cognitive');
  const isKorean = language === 'ko';
  const activityTitle = isKorean ? (activity?.titleKo || '색상 순서 기억') : (activity?.titleEn || 'Color Sequence Drum');
  const activityDescription = isKorean
    ? (activity?.descriptionKo || '색상 카드를 기억한 뒤 같은 순서로 드럼을 연주하는 활동')
    : (activity?.descriptionEn || 'Remember the color cards and play matching drums in order');

  const [profile, setProfile] = useState<MemoryAgeProfile>(MEMORY_AGE_PROFILES[0]);
  const [started, setStarted] = useState(false);
  const [trialIndex, setTrialIndex] = useState(0);
  const [span, setSpan] = useState(1);
  const [phase, setPhase] = useState<Phase>('show');
  const [visibleIndex, setVisibleIndex] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);
  const [trials, setTrials] = useState<CompletedTrial[]>([]);
  const [feedback, setFeedback] = useState('');
  const [report, setReport] = useState<CognitiveReport | null>(null);
  const [answerEffect, setAnswerEffect] = useState<AnswerEffectResult>(null);
  const handledMidiAt = useRef<number | null>(null);
  const answerTimes = useRef<number[]>([]);
  const answerStartedAt = useRef(0);
  const inputMode = useRef<'touch' | 'midi'>('touch');
  const locked = useRef(false);
  const sequence = useMemo(() => makeMemorySequence(profile, trialIndex, span), [profile, trialIndex, span]);

  useEffect(() => {
    if (!started || phase !== 'show') return undefined;
    setVisibleIndex(0);
    const interval = window.setInterval(() => setVisibleIndex((value) => value + 1), profile.cardMs);
    const finish = window.setTimeout(() => {
      window.clearInterval(interval);
      setPhase('gap');
      setFeedback(isKorean ? '색상 순서를 마음속으로 기억해 보세요' : 'Keep the color sequence in mind');
    }, sequence.length * profile.cardMs);
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(finish);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, profile.cardMs, sequence.length, started]);

  useEffect(() => {
    if (!started || phase !== 'gap') return undefined;
    const timer = window.setTimeout(() => {
      answerTimes.current = [];
      setAnswers([]);
      answerStartedAt.current = Date.now();
      setFeedback(isKorean ? '기억한 순서대로 드럼을 두드려 주세요' : 'Play the drums in the remembered order');
      setPhase('answer');
    }, profile.gapMs * span);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, profile.gapMs, span, started]);

  const moveToNext = (correct: boolean, errorType: 'sequence' | 'timeout' | null = null) => {
    if (locked.current) return;
    locked.current = true;
    const effect: AnswerEffectResult = correct ? 'correct' : 'wrong';
    setAnswerEffect(effect);
    playCognitiveAnswerTone(effect);
    const completed: CompletedTrial = { completed: true, sequence, correct, errorType, responseTimes: answerTimes.current, inputMode: inputMode.current };
    const nextTrials = [...trials, completed];
    setTrials(nextTrials);
    setPhase('transition');
    setFeedback('');
    const recent = nextTrials.slice(-3);
    const correctCount = recent.filter((trial) => trial.correct).length;
    const nextSpan = recent.length === 3 && correctCount >= 2
      ? Math.min(profile.maxSpan, span + 1)
      : recent.length === 3 && correctCount <= 1
        ? Math.max(profile.startSpan, span - 1)
        : span;
    window.setTimeout(() => {
      if (nextTrials.length >= profile.trials) {
        const nextReport = createCognitiveReport({
          activityId: ACTIVITY_ID,
          activityVersion: ACTIVITY_VERSION,
          ageProfile: profile,
          inputMode: inputMode.current,
          trials: nextTrials,
          previousReport: readPreviousReport(ACTIVITY_ID, profile.id),
          feedbackHistory: readFeedbackHistory(),
          language,
        });
        saveCognitiveReport(nextReport);
        setReport(nextReport);
        setPhase('complete');
        return;
      }
      setSpan(nextSpan);
      setTrialIndex((value) => value + 1);
      setVisibleIndex(0);
      setAnswers([]);
      setFeedback('');
      setAnswerEffect(null);
      locked.current = false;
      setPhase('show');
    }, correct ? 620 : 780);
  };

  useEffect(() => {
    if (!started || phase !== 'answer') return undefined;
    const timer = window.setTimeout(() => moveToNext(false, 'timeout'), profile.answerLimit * 1000);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, profile.answerLimit, started]);

  const handleHit = (drumId: string, source: 'touch' | 'midi' = 'touch') => {
    if (phase !== 'answer') return;
    inputMode.current = source;
    const nextAnswers = [...answers, drumId];
    answerTimes.current = [...answerTimes.current, Date.now() - answerStartedAt.current];
    if (drumId !== sequence[answers.length]) {
      moveToNext(false, 'sequence');
      return;
    }
    setAnswers(nextAnswers);
    if (nextAnswers.length === sequence.length) moveToNext(true);
  };

  useEffect(() => {
    if (!started || !midi.lastHit || phase !== 'answer' || handledMidiAt.current === midi.lastHit.at) return;
    handledMidiAt.current = midi.lastHit.at;
    handleHit(midi.lastHit.drumId, 'midi');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [midi.lastHit, phase, started]);

  const launch = () => {
    locked.current = false;
    handledMidiAt.current = midi.lastHit?.at || null;
    setTrialIndex(0);
    setSpan(profile.startSpan);
    setTrials([]);
    setAnswers([]);
    setReport(null);
    setFeedback('');
    setAnswerEffect(null);
    setStarted(true);
    setPhase('show');
  };

  if (!started) {
    return (
      <main className="cognition-game cognition-level-select" data-localized-root>
        <header className="cognition-game-header">
          <div><Brain weight="duotone" /><span><small>{isKorean ? '인지 학습 01' : 'COGNITIVE LEARNING 01'}</small><strong>{activityTitle}</strong></span></div>
          <MidiStatus language={language} status={midi.status} deviceName={midi.deviceName} onScan={midi.scan} />
          <button type="button" className="cognition-exit" onClick={onExit}><X weight="bold" />{isKorean ? '종료' : 'EXIT'}</button>
        </header>
        <section className="cognition-launch-v2">
          <div className="cognition-level-intro">
            <span>{isKorean ? '연령 기준 선택' : 'AGE REFERENCE'}</span>
            <h1>{isKorean ? '학습 기준을 선택하세요' : 'Choose a learning reference'}</h1>
            <p>{activityDescription}</p>
          </div>
          <div className="age-profile-grid">
            {MEMORY_AGE_PROFILES.map((item) => (
              <button type="button" key={item.id} className={profile.id === item.id ? 'active' : ''} aria-pressed={profile.id === item.id} onClick={() => setProfile(item)}>
                <b>{profile.id === item.id && <Check weight="bold" />}</b>
                <strong>{isKorean ? item.ko : item.en}</strong>
                <small>{isKorean ? item.detailKo : `${item.trials} items · up to ${item.maxSpan}`}</small>
              </button>
            ))}
          </div>
          <aside className="cognition-protocol-note">
            <ShieldCheck weight="duotone" />
            <span>
              <strong>{isKorean ? '학습 기록 안내' : 'Learning record'}</strong>
              <small>{isKorean ? '이번 결과는 학습 수행 기록입니다. 연령별 기준 비교는 운영 기준 데이터가 연결된 뒤 적용됩니다.' : 'Age-reference comparisons require a validated reference dataset.'}</small>
            </span>
          </aside>
          <button type="button" className="cognition-launch-start" onClick={launch}><Play weight="fill" />{isKorean ? '학습 시작' : 'START LEARNING'}</button>
        </section>
      </main>
    );
  }

  if (phase === 'complete' && report) {
    return (
      <CognitiveReportView
        title={activityTitle}
        profileLabel={isKorean ? profile.ko : profile.en}
        report={report}
        onExit={onExit}
        onReplay={launch}
        language={language}
      />
    );
  }

  const shownSequence = phase === 'show' ? sequence.map((drumId) => DRUMS.find((drum) => drum.id === drumId)!) : [];

  return (
    <main className="color-drum-game color-memory-game" data-localized-root>
      <CognitiveAnswerEffect result={answerEffect} language={language} />
      <header className="color-drum-game-header">
        <div className="color-drum-title">
          <Brain weight="duotone" />
          <span><small>{isKorean ? '인지 학습 01 · 색상 순서 기억' : 'COGNITIVE 01 · COLOR SEQUENCE'}</small><strong>{isKorean ? profile.ko : profile.en} · {trialIndex + 1}/{profile.trials}</strong></span>
        </div>
        <div className="color-drum-stats">
          <span>{isKorean ? '기억 길이' : 'SPAN'}<strong>{span}/{profile.maxSpan}</strong></span>
          <span>{isKorean ? '정답' : 'CORRECT'}<strong>{trials.filter((trial) => trial.correct).length}</strong></span>
        </div>
        <MidiStatus language={language} status={midi.status} deviceName={midi.deviceName} onScan={midi.scan} />
        <button type="button" className="cognition-exit" onClick={onExit}><X weight="bold" />{isKorean ? '종료' : 'EXIT'}</button>
      </header>
      <section className="color-drum-stage memory-stage">
        <div className="color-drum-instruction">
          <div>
            <span>{phase === 'show' ? (isKorean ? '색상 관찰' : 'WATCH') : phase === 'gap' ? (isKorean ? '순서 기억' : 'REMEMBER') : (isKorean ? '순서 입력' : 'PLAY')}</span>
            <h1>{phase === 'show' ? (isKorean ? '색상 순서를 보고 기억하세요' : 'Watch and remember the sequence') : phase === 'gap' ? (isKorean ? '회색 원에 색상 순서를 떠올려 보세요' : 'Recall the colors in the gray circles') : (isKorean ? '기억한 순서대로 드럼을 연주하세요' : 'Play the remembered sequence')}</h1>
          </div>
          <p>{isKorean ? `최대 ${profile.maxSpan}개 · 오답도 다음 문제로 진행` : `Up to ${profile.maxSpan} items · wrong answers advance`}</p>
        </div>
        <div className={`memory-card-stage memory-sequence-stage ${phase}`} aria-live="polite">
          {phase === 'show' ? (
            <div className="memory-sequence-row">
              {shownSequence.map((drum, index) => (
                <i key={`${trialIndex}-${index}`} className={`memory-sequence-dot ${index <= visibleIndex ? 'revealed' : 'waiting'}`} style={{ '--drum-color': drum.color } as React.CSSProperties} aria-label={isKorean ? drum.ko : drum.en} />
              ))}
            </div>
          ) : (
            <div className={`memory-recall-row ${phase}`}>
              {sequence.map((_, index) => {
                const selectedDrum = answers[index] ? DRUMS.find((drum) => drum.id === answers[index]) : null;
                return (
                  <i
                    key={index}
                    className={selectedDrum ? 'filled' : ''}
                    style={selectedDrum ? ({ '--drum-color': selectedDrum.color } as React.CSSProperties) : undefined}
                    aria-label={selectedDrum ? (isKorean ? selectedDrum.ko : selectedDrum.en) : (isKorean ? '기억할 색상 위치' : 'Color memory slot')}
                  />
                );
              })}
            </div>
          )}
        </div>
        <div className="color-drum-feedback">{feedback || (isKorean ? '첫 번째 색상을 관찰하세요' : 'Watch the first color')}</div>
        <div className="drum-pad-grid">
          {DRUMS.map((drum) => (
            <button type="button" data-drum-id={drum.id} className="cognitive-drum-pad" style={{ '--drum-color': drum.color } as React.CSSProperties} key={drum.id} aria-disabled={phase !== 'answer'} onPointerDown={() => handleHit(drum.id)} aria-label={isKorean ? drum.ko : drum.en}>
              <span className="drum-pad-face" />
            </button>
          ))}
        </div>
      </section>
      <footer className="color-drum-progress"><span><i style={{ width: `${(trialIndex / profile.trials) * 100}%` }} /></span><strong>{trialIndex} / {profile.trials}</strong></footer>
    </main>
  );
}

export default ColorCognitionGame;
